import React from 'react';
import {
  Box,
  Grid,
  Typography,
  Paper,
  Chip,
  Divider
} from '@mui/material';
import { 
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  Home as HomeIcon,
  CalendarToday as CalendarIcon,
  Update as UpdateIcon,
  ElectricBolt as ElectricIcon
} from '@mui/icons-material';
import { Property } from '../../types/property.types';
import type { ChipProps } from '@mui/material';

interface PropertyInfoProps {
  property: Property;
}

const PropertyInfo: React.FC<PropertyInfoProps> = ({ property }) => {
  const formatAddress = (property: Property) => {
    const parts = [
      property.address.line1,
      property.address.line2,
      property.address.city,
      property.address.county,
      property.address.postcode,
      property.address.country
    ].filter(Boolean);
    return parts.join(', ');
  };


  return (
    <Paper sx={{ 
      p: 3, 
      borderRadius: 2, 
      backdropFilter: 'blur(12px)',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
    }}>
      <Box mb={3}>
        <Typography variant="h5" gutterBottom sx={{
          borderLeft: '4px solid #3b82f6',
          pl: 2,
          py: 0.5,
          color: 'text.primary'
        }}>
          {property.name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
          <Chip
            label={property.utilityServices.length > 0 ? 'Active Services' : 'No Services'}
            color={property.utilityServices.length > 0 ? 'success' : 'default'}
            size="small"
            sx={{ 
              borderRadius: '0.375rem',
              fontWeight: 500
            }}
          />
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Address Section */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" gutterBottom sx={{ 
            borderLeft: '4px solid #10b981', 
            pl: 2,
            py: 0.5,
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            borderRadius: '0 4px 4px 0',
            color: 'text.primary'
          }}>Address</Typography>
          
          <Paper elevation={1} sx={{ 
            p: 2, 
            borderRadius: 2, 
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            mt: 1 
          }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <LocationIcon sx={{ mr: 2, color: 'primary.light', mt: 0.5 }} />
              <Typography variant="body1" color="text.primary">{formatAddress(property)}</Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Contact Information Section */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" gutterBottom sx={{ 
            borderLeft: '4px solid #f59e0b', 
            pl: 2,
            py: 0.5,
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            borderRadius: '0 4px 4px 0',
            color: 'text.primary'
          }}>Contact Information</Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Paper elevation={1} sx={{ 
              p: 2, 
              borderRadius: 2, 
              backdropFilter: 'blur(8px)',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonIcon sx={{ mr: 2, color: 'primary.light' }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Contact Name</Typography>
                  <Typography variant="body1" color="text.primary">{property.contactName || 'Not specified'}</Typography>
                </Box>
              </Box>
            </Paper>
            
            <Paper elevation={1} sx={{ 
              p: 2, 
              borderRadius: 2, 
              backdropFilter: 'blur(8px)',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EmailIcon sx={{ mr: 2, color: 'primary.light' }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Contact Email</Typography>
                  <Typography variant="body1" color="text.primary">{property.contactEmail || 'Not specified'}</Typography>
                </Box>
              </Box>
            </Paper>
            
            <Paper elevation={1} sx={{ 
              p: 2, 
              borderRadius: 2, 
              backdropFilter: 'blur(8px)',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PhoneIcon sx={{ mr: 2, color: 'primary.light' }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Contact Phone</Typography>
                  <Typography variant="body1" color="text.primary">{property.contactPhone || 'Not specified'}</Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Grid>

        {/* Notes Section */}
        {property.notes && (
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ 
              borderLeft: '4px solid #8b5cf6', 
              pl: 2,
              py: 0.5,
              backgroundColor: 'rgba(139, 92, 246, 0.1)',
              borderRadius: '0 4px 4px 0',
              color: 'text.primary'
            }}>Notes</Typography>
            
            <Paper elevation={1} sx={{ 
              p: 2, 
              borderRadius: 2, 
              backdropFilter: 'blur(8px)',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              mt: 1 
            }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <DescriptionIcon sx={{ mr: 2, color: 'primary.light', mt: 0.5 }} />
                <Typography variant="body1" color="text.primary">{property.notes}</Typography>
              </Box>
            </Paper>
          </Grid>
        )}

        <Grid item xs={12}>
          <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
        </Grid>

        {/* Property Details Section */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" gutterBottom sx={{ 
            borderLeft: '4px solid #3b82f6', 
            pl: 2,
            py: 0.5,
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            borderRadius: '0 4px 4px 0',
            color: 'text.primary'
          }}>Property Details</Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Paper elevation={1} sx={{ 
              p: 2, 
              borderRadius: 2, 
              backdropFilter: 'blur(8px)',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CalendarIcon sx={{ mr: 2, color: 'primary.light' }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Created</Typography>
                  <Typography variant="body1" color="text.primary">{new Date(property.createdAt).toLocaleDateString()}</Typography>
                </Box>
              </Box>
            </Paper>
            
            <Paper elevation={1} sx={{ 
              p: 2, 
              borderRadius: 2, 
              backdropFilter: 'blur(8px)',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <UpdateIcon sx={{ mr: 2, color: 'primary.light' }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Last Updated</Typography>
                  <Typography variant="body1" color="text.primary">{new Date(property.updatedAt).toLocaleDateString()}</Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Grid>

        {/* Utility Services Section */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" gutterBottom sx={{ 
            borderLeft: '4px solid #ef4444', 
            pl: 2,
            py: 0.5,
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            borderRadius: '0 4px 4px 0',
            color: 'text.primary'
          }}>Utility Services</Typography>
          
          <Paper elevation={1} sx={{ 
            p: 2, 
            borderRadius: 2, 
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            mt: 1 
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ElectricIcon sx={{ mr: 2, color: 'primary.light' }} />
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Connected Services</Typography>
                <Typography variant="body1" color="text.primary">{property.utilityServices.length} service(s) connected</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default PropertyInfo;
